/**
 * PLC access base: the wire contract. FROZEN, owned by the orchestrator.
 *
 * Communities (locations), groups of communities, and departments decide which jobs, and
 * through them which applicants, interviews and offers, a person can see. One rule on the
 * server enforces it, and every screen reads the same options from `GET /me/access-scope`.
 *
 * Rules every endpoint below follows:
 * - Empty means all (decision 6). A user with no community rows (direct or group) sees every
 *   community; no department rows sees every department within them.
 * - Groups resolve at request time: adding a community to a group widens every holder at once.
 * - Holding rows that resolve to nothing (a group whose communities were all removed) means
 *   NOTHING, never all. Scope fails closed.
 * - A filter never widens. `locationId` / `groupId` / `departmentId` are intersected with the
 *   caller's scope; an id outside it yields an empty result, not an error and not a wider one.
 * - Departments are identified by `job_posting_options.id` (UUID) everywhere on the wire.
 *   `key` is that row's `value`: readable, unique per instance, and immutable once created.
 */

// ── Role data scope ─────────────────────────────────────────────────────────────

/**
 * What slice of hiring data a role reaches, stored on the role (`public.roles.data_scope`),
 * never inferred from its name, so custom and per-instance copies of roles work.
 *
 * - `all`                   admin, skypoint_admin, legacy super_admin. Never restricted.
 * - `communities`           team_member and every custom role. Restricted by community/department rows.
 * - `assigned_jobs`         limited_team_member: directly assigned jobs ∩ communities ∩ departments.
 * - `assigned_applications` interviewer: assigned applications only.
 * - `client_shares`         client: explicitly or automatically shared jobs/applications only.
 * - `none`                  employee: no hiring data (the referral hub is outside this rule, decision 4).
 */
export const DATA_SCOPES = [
  "all",
  "communities",
  "assigned_jobs",
  "assigned_applications",
  "client_shares",
  "none",
] as const;
export type DataScope = (typeof DATA_SCOPES)[number];

/** Roles an admin can restrict by community and department (decision 5). */
export function isScopableDataScope(dataScope: DataScope | null | undefined): boolean {
  return dataScope === "communities" || dataScope === "assigned_jobs";
}

// ── Filters (the two dropdowns) ─────────────────────────────────────────────────

/**
 * Query params a scoped list/aggregate endpoint accepts. Omit a param for "All".
 * `locationId` and `groupId` are mutually exclusive: both present → 400 SCOPE_FILTER_CONFLICT.
 * Adopted in Phase B by: applicants list + stats + positions, jobs list, reports + filter options,
 * interviews, offers, dashboard + insights, and the email/SMS inbox and its counts (D30).
 */
export interface ScopeFilterParams {
  locationId?: number;
  groupId?: string;
  departmentId?: string;
}

/** Client URL state, one value per dropdown. Anything unparseable or not in the options → "all". */
export type LocationFilterValue = "all" | `group:${string}` | `location:${number}`;
export type DepartmentFilterValue = "all" | string; // "all" or a department id
export const SCOPE_URL_PARAMS = { location: "loc", department: "dept" } as const;
/** localStorage key for the last choice, per instance. Read/write inside try/catch. */
export const scopeStorageKey = (instanceId: string) => `skyhire:scope:${instanceId}`;

export interface ScopeLocationOption {
  id: number;
  name: string;
}

export interface ScopeGroupOption {
  id: string;
  name: string;
  /** Active and inactive members, as the server resolves them. */
  locationIds: number[];
}

export interface ScopeDepartmentOption {
  id: string;
  label: string;
}

/**
 * `GET /api/v1/me/access-scope?instanceId=<uuid>`: the options for the caller's dropdowns,
 * already intersected with their scope. The client only ever picks inside this.
 *
 * Render `<ScopeFilters>` only when `dataScope` is `all`, `communities` or `assigned_jobs`.
 * Location dropdown: hidden when `locations.options.length <= 1`.
 * Department dropdown: shown only when `departments.enabled`; hidden when
 * `departments.options.length <= 1` for a restricted user.
 */
export interface MeAccessScopeResponse {
  instanceId: string;
  dataScope: DataScope;
  locations: {
    /** True when the caller has no community rows ("All locations"); false → "All my locations". */
    unrestricted: boolean;
    /** Active communities in scope, sorted by name. */
    options: ScopeLocationOption[];
  };
  /** Unrestricted: every group in the instance. Restricted: the groups the caller holds. Sorted by name. */
  groups: ScopeGroupOption[];
  departments: {
    /** At least one active department AND at least one job in the instance with a valid department. */
    enabled: boolean;
    unrestricted: boolean;
    /** Active departments in scope, by sort order. */
    options: ScopeDepartmentOption[];
  };
}

// ── Location groups (Settings → Locations → Groups) ──────────────────────────────

/**
 * `GET    /api/v1/location-groups?instanceId`        → { groups: LocationGroup[] }   instance access
 * `POST   /api/v1/location-groups?instanceId`        body LocationGroupInput → 201 LocationGroup   settings.manage
 * `PUT    /api/v1/location-groups/:id?instanceId`    body LocationGroupInput (full replace) → LocationGroup
 * `DELETE /api/v1/location-groups/:id?instanceId`    → 204 | 409 GROUP_IN_USE
 *
 * Errors: 400 GROUP_EMPTY (no locationIds), 400 SCOPE_INVALID_IDS (unknown location ids),
 * 409 GROUP_NAME_TAKEN (case/space-insensitive), 404 GROUP_NOT_FOUND.
 * DELETE is refused only while the group is some user's ONLY community scope (deleting it
 * would widen them to all); otherwise the holders' rows go in the same transaction.
 */
export interface LocationGroup {
  id: string;
  name: string;
  locationIds: number[];
  /** Users in this instance holding the group. Null for a caller who does not reach the whole instance. */
  userCount: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface LocationGroupInput {
  name: string;
  locationIds: number[];
}

export interface GroupInUseError {
  error: string;
  code: "GROUP_IN_USE";
  /** Lowercased emails whose only community scope is this group. */
  users: string[];
}

// ── Departments (Settings → Departments) ───────────────────────────────────────

/**
 * `GET   /api/v1/departments?instanceId[&includeInactive=true]` → { departments: Department[] }   instance access
 * `POST  /api/v1/departments?instanceId`             body DepartmentCreate → 201 Department   settings.manage
 * `PUT   /api/v1/departments/:id?instanceId`         body DepartmentUpdate → Department       (label rename rewrites job text)
 * `PATCH /api/v1/departments/:id/active?instanceId`  body { isActive: boolean } → Department
 * `PATCH /api/v1/departments/reorder?instanceId`     body { ids: string[] } → { departments: Department[] }
 * No DELETE. Deactivate instead.
 *
 * Errors: 409 DEPARTMENT_KEY_TAKEN, 409 DEPARTMENT_LABEL_TAKEN, 400 DEPARTMENT_KEY_IMMUTABLE
 * (a body carrying `key` on PUT), 404 DEPARTMENT_NOT_FOUND.
 * The old `/api/v1/job-posting-options` routes keep serving the other categories; for a
 * department row they refuse a `value` change (400 DEPARTMENT_KEY_IMMUTABLE) and DELETE
 * (409 DEPARTMENT_DELETE_NOT_ALLOWED).
 *
 * An inactive department leaves the job form and the filters, but still counts as a valid
 * department on existing jobs and in users' scope (it does not hide jobs, decision 3).
 */
export interface Department {
  id: string;
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  /**
   * Jobs using this department, instance-wide. Null for callers without `settings.view`
   * (instance-wide counts are settings data, not something every member may see).
   */
  jobCount: number | null;
  /** Users in this instance restricted to this department. Null like `jobCount`. */
  userCount: number | null;
}

export interface DepartmentCreate {
  label: string;
  /** Optional; slugged from the label when omitted (lowercase a-z0-9_). Immutable afterwards. */
  key?: string;
}

export interface DepartmentUpdate {
  label: string;
}

// ── Jobs to fix (admin list, decision 3 + jobs without a community) ──────────────

/**
 * `GET /api/v1/jobs-to-fix?instanceId` → JobsToFixResponse   settings.manage, unrestricted caller
 * `PUT /api/v1/jobs-to-fix?instanceId` body JobsToFixPut → { updated: number }
 *
 * department: `department_id` is NULL or points at no department row in this instance.
 * location:   `location_id` is NULL or points at no location row.
 * Grouped by the job's current text (`department`, or `location_name`), null for empty.
 * A mapping applies to every job in its group, in one transaction.
 */
export interface JobToFix {
  id: string;
  title: string;
  status: string;
  department: string | null;
  locationName: string | null;
}

export interface JobFixGroup {
  currentValue: string | null;
  jobCount: number;
  jobs: JobToFix[];
  /**
   * What the server believes this text means, so an admin confirms rather than hunts:
   * `name` when a department label/key or a community name matches the text (case and spacing
   * ignored), `city_state` when every job in the group shares one community's city and state.
   * Null when nothing matches — for a community that is the case to offer "create" (below).
   */
  suggestion?: {
    departmentId?: string;
    locationId?: number;
    reason: "name" | "city_state";
  } | null;
}

export interface JobsToFixResponse {
  department: JobFixGroup[];
  location: JobFixGroup[];
  totals: { department: number; location: number };
}

export type JobsToFixPut =
  | { kind: "department"; mappings: { currentValue: string | null; departmentId: string }[] }
  | {
      kind: "location";
      /**
       * Either map the group to a community, or create one FROM the text the jobs already carry
       * (`newLocation`) and map them to it. Creating is what makes the screen usable on a tenant
       * whose communities were never entered: the jobs name them, so the admin confirms instead
       * of typing each one. Exactly one of `locationId` and `newLocation` per entry.
       */
      mappings: {
        currentValue: string | null;
        locationId?: number;
        newLocation?: { name: string; city?: string | null; state?: string | null };
      }[];
    };

/** PUT answer. `createdLocations` names what a `newLocation` entry created, for the toast. */
export interface JobsToFixResult {
  updated: number;
  /**
   * One entry per `newLocation`. When a community of that name already existed but was switched
   * off, it is REUSED and switched back on rather than duplicated (two communities of one name
   * would split everyone's access), and comes back with `reactivated: true` so the screen can
   * say so.
   */
  createdLocations?: { id: number; name: string; reactivated?: boolean }[];
}

// ── User scope (Users page → Edit access) ──────────────────────────────────────

/**
 * - `not_applicable`  the effective role is not scopable (admin, interviewer, client, employee).
 * - `scoped`          at least one community, group or department row.
 * - `unrestricted`    scopable role, no rows, and nobody flagged it pending.
 * - `not_scoped_yet`  scopable role, no rows, and the role was granted or changed since the
 *                     base shipped without an admin deciding. A FLAG only: access is still
 *                     "empty means all" (decisions 6 and 7). Existing users are never flagged.
 */
export type ScopeStatus = "not_applicable" | "scoped" | "unrestricted" | "not_scoped_yet";

/**
 * `GET /api/v1/admin/rbac/users/:email/scope?instanceId` → UserScope   users.manage
 * `PUT /api/v1/admin/rbac/users/:email/scope?instanceId` body UserScopePut → UserScope
 *
 * PUT replaces all three sets and clears the pending flag in ONE transaction.
 * Errors:
 * - 400 SCOPE_CHOICE_REQUIRED  every array empty and `unrestricted` false, or a non-empty array with `unrestricted` true
 * - 400 SCOPE_INVALID_IDS      { invalid: { locationIds, groupIds, departmentIds } } unknown in this instance
 * - 403 SCOPE_EXCEEDS_EDITOR   a restricted editor granting outside their own scope, or granting `unrestricted`
 * - 409 ROLE_NOT_SCOPABLE      the user's effective role in the instance is not scopable
 * - 404 USER_NOT_IN_INSTANCE   no role reaches this instance
 * - 403 SELF_MODIFICATION_NOT_ALLOWED  the caller is the target
 * - 400 INVALID_REQUEST        body or instanceId failed validation
 * Save order for a combined editor: role PATCH first, then scope PUT.
 */
export interface UserScope {
  instanceId: string;
  userEmail: string;
  dataScope: DataScope | null;
  status: ScopeStatus;
  locationIds: number[];
  groupIds: string[];
  departmentIds: string[];
}

export interface UserScopePut {
  locationIds: number[];
  groupIds: string[];
  departmentIds: string[];
  /** Explicit "Can see everything". Required when all three arrays are empty. */
  unrestricted: boolean;
}

/**
 * Added to each user row of `GET /api/v1/admin/rbac/users` as `scope`, for the instance the
 * list is requested for (no N+1). Names are for the Access column and its filter.
 */
export interface UserScopeSummary {
  status: ScopeStatus;
  locations: ScopeLocationOption[];
  groups: { id: string; name: string }[];
  departments: ScopeDepartmentOption[];
}

// ── Errors ────────────────────────────────────────────────────────────────────

export const ACCESS_SCOPE_ERROR_CODES = [
  "ACCESS_SCOPE_UNAVAILABLE", // 503: the scope could not be resolved; never served unrestricted
  "SCOPE_FILTER_CONFLICT",
  "SCOPE_CHOICE_REQUIRED",
  "SCOPE_INVALID_IDS",
  "SCOPE_EXCEEDS_EDITOR",
  "ROLE_NOT_SCOPABLE",
  "USER_NOT_IN_INSTANCE",
  "SELF_MODIFICATION_NOT_ALLOWED", // 403: nobody edits their own access
  "INVALID_REQUEST", // 400: body or instanceId failed validation
  "GROUP_EMPTY",
  "GROUP_NAME_TAKEN",
  "GROUP_NOT_FOUND",
  "GROUP_IN_USE",
  "DEPARTMENT_KEY_TAKEN",
  "DEPARTMENT_LABEL_TAKEN",
  "DEPARTMENT_KEY_IMMUTABLE",
  "DEPARTMENT_DELETE_NOT_ALLOWED",
  "DEPARTMENT_NOT_FOUND",
  "JOB_PLACEMENT_OUT_OF_SCOPE", // 403: creating/moving a job outside the caller's scope
  "UNRESTRICTED_ACCESS_REQUIRED", // 403: an instance-wide action by a caller limited by community or department (D29)
  "HRIS_MANAGED", // 409: the HRIS feed sets this person's access (hris_scope_subjects); turn that off first
] as const;
export type AccessScopeErrorCode = (typeof ACCESS_SCOPE_ERROR_CODES)[number];

export interface AccessScopeErrorBody {
  error: string;
  code: AccessScopeErrorCode;
}

/**
 * Why a signed-in caller cannot use an instance (Phase U, deny-by-default).
 *
 * Instance routes answer 403 `{ error, code }` with one of these (server/middleware/auth.ts
 * verifyTenantAccess, sent by tenantAccess.enforceInstanceAccess). POST /auth/resolve-instance keeps
 * its 200 `{ hasAccess: false }` shape for existing clients and adds `code`; a blocked account stays a
 * 403 there. The client shows one "no access" screen per code.
 */
export const TENANT_ACCESS_DENIAL_CODES = [
  "NOT_AUTHENTICATED",
  "NO_ACCOUNT",
  "NO_ASSIGNMENT",
  "NO_PRODUCT_ACCESS",
  "ACCOUNT_BLOCKED",
  "INSTANCE_NOT_FOUND",
  "CHECK_FAILED",
] as const;

export type TenantAccessDenialCode = (typeof TENANT_ACCESS_DENIAL_CODES)[number];

export interface TenantAccessDenialBody {
  error: string;
  code: TenantAccessDenialCode;
}

/** POST /auth/resolve-instance when the caller cannot use the instance (additive `code`). */
export interface ResolveInstanceDenied {
  hasAccess: false;
  code: Exclude<TenantAccessDenialCode, "ACCOUNT_BLOCKED" | "NOT_AUTHENTICATED">;
}
